/** @jsx jsx */
import Logo from '@zapier/product-dev-platform/src/components/Logo';
import { Button, Icon, Media, SkipNav } from '@zapier/common-components';
import { Colors, Typography } from '@zapier/theme';
import {
  LOGIN_URL,
  MEDIA_MIN_DESKTOP,
  PLATFORM_ROOT,
  SIGNUP_URL,
  navLinkBaseCss,
} from '@zapier/product-dev-platform/src/constants';
import { css, jsx, Interpolation } from '@emotion/core';
import CtaLinks from './CtaLinks';
import Nav from './Nav';
import UserOptions from './UserOptions';
import { HeaderNav, HeaderUser } from '@zapier/product-dev-platform/src/types';
import type { NavItem } from './types';

type LayoutProps = {
  isNavOpen: boolean;
  isUserOptionsOpen: boolean;
  onToggleNav: () => void;
  onToggleUserOptions: () => void;
  nav: HeaderNav;
  ctaLinks: HeaderNav;
  user: HeaderUser | undefined | null;
  userLinks: Array<NavItem>;
  /** Location of the website url */
  urlPath?: string;
  logoUrl?: string;
};
const defaultProps = {
  isFullWidth: false,
  ctaLinks: [],
  userLinks: [],
  logoUrl: PLATFORM_ROOT,
};
const defaultFlex = {
  display: 'flex',
  alignItems: 'center',
};
const wrapperCss = {
  backgroundColor: Colors.blackWidow,
  ...Typography.body,
  color: Colors.adamantium,
  padding: '0 30px',

  /** This 1px is taken into account in property top of rightSplitCss. */
  borderBottom: 'solid 1px rgba(255, 255, 255, 0.1)',
};
const containerCss: Interpolation = {
  ...defaultFlex,
  justifyContent: 'space-between',
  position: 'relative',
  height: 55,
  margin: 'auto',
  [MEDIA_MIN_DESKTOP]: {
    height: 68,
  },
};

const rightSplitCss = (props: LayoutProps): Interpolation => ({
  position: 'absolute',

  /** This 1px is an offset to property bottom-border of wrapperCss. */
  top: 'calc(100% + 1px)',
  left: -20,
  right: -20,
  zIndex: 1,
  backgroundColor: Colors.white,
  display: props.isNavOpen ? 'flex' : 'none',
  flexDirection: 'column',
  [MEDIA_MIN_DESKTOP]: {
    ...defaultFlex,
    flexDirection: 'row',
    flex: '1 0 auto',
    alignSelf: 'stretch',
    position: 'relative',
    top: 'auto',
    left: 'auto',
    right: 'auto',
    backgroundColor: 'transparent',
    marginLeft: 40,
  },
});

const ctaButtonsCss: Interpolation = {
  display: 'flex',
  '@media (max-width: 1079px)': {
    justifyContent: 'space-around',
    flexDirection: 'row-reverse',
    borderBottom: `1px solid ${Colors.adamantium}`,
    padding: '1.5em',
  },
  a: {
    marginLeft: 8,
    '&:hover': {
      // Overwrite some global styles in web
      textDecoration: 'none',
    },
  },
};
const onlyMobileCss = {
  [MEDIA_MIN_DESKTOP]: {
    display: 'none',
  },
};
const logoCss: Interpolation = {
  display: 'flex',
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  [MEDIA_MIN_DESKTOP]: {
    position: 'static',
    transform: 'none',
  },
};

const CtaButtons = () => (
  <Media queries={{ small: '(max-width: 1079px)' }}>
    {({ small }: { small: boolean }) => (
      <nav css={ctaButtonsCss}>
        <Button
          color={!small ? 'platformDark' : 'secondary'}
          hoverInvert={!small}
          href={LOGIN_URL}
          isFullWidth={small}
          isSlim={!small}
          size="medium"
        >
          Log In
        </Button>
        <Button
          color="important"
          href={SIGNUP_URL}
          isFullWidth={small}
          isSlim={!small}
          size="medium"
        >
          Sign Up
        </Button>
      </nav>
    )}
  </Media>
);

const Layout = (props: LayoutProps) => (
  <header css={wrapperCss}>
    <div css={containerCss}>
      <Icon
        css={onlyMobileCss}
        name={props.isNavOpen ? 'delete' : 'menu'}
        onClick={props.onToggleNav}
      />
      <a
        aria-label="Zapier Platform"
        css={logoCss}
        href={props.logoUrl}
        tabIndex={0}
      >
        <Logo />
      </a>
      <SkipNav
        ariaLabel="Skip to main content"
        skipToSelector={'main'}
        urlPath={props.urlPath}
      />
      <div css={rightSplitCss(props)}>
        <Nav config={props.nav} />
        {!props.user ? <CtaButtons /> : <CtaLinks links={props.ctaLinks} />}
      </div>
      {!props.user && (
        <a css={css(onlyMobileCss, navLinkBaseCss)} href={LOGIN_URL}>
          Log In
        </a>
      )}
      {props.user && (
        <UserOptions
          ctaLinks={props.ctaLinks}
          isUserOptionsOpen={props.isUserOptionsOpen}
          onToggleUserOptions={props.onToggleUserOptions}
          optionsLinks={props.userLinks}
          user={props.user}
        />
      )}
    </div>
  </header>
);

Layout.defaultProps = defaultProps;
export default Layout;
